import { DOCUMENT } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { PageDataService } from './core/page-data.service';
import { RealtimeService } from './core/realtime.service';
import { SiteConfigService } from './core/site-config.service';
import { ScrollTopComponent } from './shared/scroll-top.component';
import { SiteFooterComponent } from './shell/site-footer.component';
import { SiteHeaderComponent } from './shell/site-header.component';
import { LiveCallOverlayComponent } from './shared/live-call-overlay.component';
import { AnalyticsService } from './core/analytics.service';
import { UnsupportedLocaleNoticeComponent } from './shared/unsupported-locale-notice.component';
import { CookieConsentComponent } from './shared/cookie-consent.component';
import { LiveSupportWidgetComponent } from './widgets/live-support/live-support-widget.component';
import { LiveSupportAgentWidgetComponent } from './widgets/live-support/live-support-agent-widget.component';
import { ThemeStorefrontComponent } from './theme/theme-storefront.component';
import { ThemePageSectionsComponent } from './theme/theme-page-sections.component';
import { GuidedActionWidgetComponent } from './widgets/guided-action/guided-action-widget.component';
import { VetSiteFooterComponent, VetSiteHeaderComponent } from './shell/vet-site-shell.component';
import { ClinicSiteFooterComponent, ClinicSiteHeaderComponent } from './shell/clinic-site-shell.component';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ScrollTopComponent, SiteFooterComponent, SiteHeaderComponent, VetSiteHeaderComponent, VetSiteFooterComponent, ClinicSiteHeaderComponent, ClinicSiteFooterComponent, LiveCallOverlayComponent, UnsupportedLocaleNoticeComponent, CookieConsentComponent, LiveSupportWidgetComponent, LiveSupportAgentWidgetComponent, ThemeStorefrontComponent, ThemePageSectionsComponent, GuidedActionWidgetComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App {
  protected readonly site = inject(SiteConfigService);
  protected readonly pageData = inject(PageDataService);
  private readonly realtime = inject(RealtimeService);
  private readonly analytics = inject(AnalyticsService);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  protected readonly brandMark = computed(() => this.site.config().branding.mark);
  protected readonly currentUrl = signal(typeof window!=='undefined' ? window.location.pathname+window.location.search : this.router.url);
  protected readonly ssoAuthWorkspace = computed(() => {
    const url=this.currentUrl().toLowerCase();
    return url.includes('sso=1')&&/\/(?:giris-yap|kayit-ol|login|register)(?:\?|$)/.test(url);
  });
  protected readonly themeStorefront = computed(() => this.pageData.has('themeRender'));
  protected readonly themePresentation = computed(() => this.pageData.get<{themeId:string;themeVersion:string;template:string;preview:boolean;stylesheet:string;tokens:Record<string,string>;page?:unknown}>('themePresentation'));
  protected readonly themeAnchorTypes = computed<string[]>(() => {
    switch (this.themePresentation()?.template) {
      case 'product': return ['product-main'];
      case 'seller': return ['seller-profile', 'page-layout'];
      case 'cart': return ['cart-shell'];
      case 'checkout': return ['checkout-shell'];
      case 'search': return ['search-results'];
      case 'feeds': return ['feed-stream', 'page-layout'];
      case 'index': return ['core-page'];
      default: return ['page-layout', 'page-intro', 'core-page'];
    }
  });
  protected readonly themePageOptions = computed<Record<string,string>>(() => {
    const presentation = this.themePresentation();
    const page = presentation?.page;
    if (!page || typeof page !== 'object' || Array.isArray(page)) return {};
    const sections = (page as {sections?:unknown}).sections;
    if (!Array.isArray(sections)) return {};
    const layoutSection = sections.find((section) => {
      if (!section || typeof section !== 'object' || Array.isArray(section)) return false;
      return (section as {type?:unknown}).type === 'page-layout';
    }) as {settings?:unknown} | undefined;
    const settings = layoutSection?.settings;
    if (!settings || typeof settings !== 'object' || Array.isArray(settings)) return {};
    return Object.fromEntries(Object.entries(settings as Record<string,unknown>).map(([key,value]) => [key,String(value ?? '')]));
  });
  protected readonly sellerWorkspace = computed(() => {
    const url = this.currentUrl().toLowerCase();
    return url.includes('/seller-panel');
  });
  protected readonly adminWorkspace = computed(() => {
    const url = this.currentUrl().toLowerCase();
    return url.includes('/admin-panel');
  });
  protected readonly vetPublicShell = computed(() =>
    this.site.config().businessMode === 'veterinaryMarketplace' &&
    !this.sellerWorkspace() && !this.adminWorkspace() &&
    !this.themeStorefront() && !this.themePresentation()
  );
  protected readonly clinicPublicShell = computed(() =>
    this.site.config().brandKey === 'klinik' &&
    !this.sellerWorkspace() && !this.adminWorkspace() &&
    !this.themeStorefront() && !this.themePresentation()
  );
  protected readonly qrMarketplaceWorkspace = computed(() => {
    if (this.site.config().brandKey !== 'qr-marketplace') return false;
    const path = this.currentUrl().toLowerCase().split('?')[0];
    return path === '/' || path === '/yonetim' || path.startsWith('/yonetim/');
  });
  protected readonly customerServiceWorkspace = computed(() => this.currentUrl().split(/[?#]/)[0].replace(/\/+$/, '') === '/customer-service');
  protected readonly themeEditorWorkspace = computed(() => {
    const path = this.currentUrl().toLowerCase().split('?')[0].replace(/\/+$/, '');
    return path.endsWith('/admin-panel/system/theme') || path.endsWith('/admin-panel/system/tema');
  });

  constructor() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl.set(event.urlAfterRedirects || event.url || this.router.url);
        if (typeof document !== 'undefined') {
          const active = document.activeElement;
          if (active instanceof HTMLElement) active.blur();
        }
      }
    });
    effect(() => {
      if (typeof document === 'undefined') {
        return;
      }

      const config = this.site.config();
      const root = document.documentElement;

      root.dataset['brand'] = config.brandKey;
      root.dataset['design'] = config.design.layout;
      const engine=config.themeEngine;
      root.dataset['themePreset']=engine.preset; root.dataset['themeHeader']=engine.header; root.dataset['themeFooter']=engine.footer;
      root.dataset['themeProduct']=engine.productDetail; root.dataset['themeCards']=engine.productCards; root.dataset['themeShape']=engine.shape;
      root.dataset['themeDensity']=engine.density; root.dataset['themeWidth']=engine.contentWidth; root.dataset['themeMotion']=engine.motion;
      root.dataset['themeDisplay']=engine.typography.display; root.dataset['themeBody']=engine.typography.body; root.dataset['themeScale']=engine.typography.scale;
      const widths={narrow:'980px',standard:'1180px',wide:'1440px',full:'calc(100vw - 32px)'} as const;
      const radii={square:'4px',soft:'16px',rounded:'30px'} as const; const spaces={compact:'.82',comfortable:'1',airy:'1.22'} as const;
      const displayFonts={serif:'Georgia, Cambria, Times New Roman, serif',sans:'Inter, Avenir Next, Arial, sans-serif',grotesk:'Arial Black, Inter, Arial, sans-serif'} as const;
      const bodyFonts={sans:'Inter, Arial, sans-serif',humanist:'Avenir Next, Segoe UI, Arial, sans-serif'} as const;
      root.style.setProperty('--theme-content-width',widths[engine.contentWidth]); root.style.setProperty('--theme-radius',radii[engine.shape]); root.style.setProperty('--theme-space',spaces[engine.density]);
      root.style.setProperty('--font-display',displayFonts[engine.typography.display]); root.style.setProperty('--font-body',bodyFonts[engine.typography.body]);
      this.updateFavicon(config.branding.iconUrl);
    });
    effect(() => {
      if (typeof window === 'undefined') return;
      const theme=this.themePresentation();
      const existing=this.document.head?.querySelector<HTMLStyleElement>('style[data-theme-core-package]');
      if(!theme){existing?.remove();return}
      const style=existing??this.document.createElement('style');style.setAttribute('data-theme-core-package',theme.themeId);if(style.textContent!==theme.stylesheet)style.textContent=theme.stylesheet;if(!style.isConnected)this.document.head?.append(style);
    });
  }

  private updateFavicon(iconUrl?: string): void {
    if (!iconUrl) {
      return;
    }
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = iconUrl;
  }
}
